/** Fn with a `.cancel()` attached that clears any pending invocation. */
export type Debounced<TArgs extends unknown[], TResult> = ((...args: TArgs) => Promise<TResult>) & {
	cancel: () => void;
};

/**
 * TODO: move this (and other utils) to component library for reuseability.
 *
 * Wrap `fn` so calls made within `delay` ms of each other collapse to the
 * last one. Earlier promises never resolve; the invoker is expected to
 * only care about the latest result. Call `.cancel()` to clear the pending
 * timer without firing (e.g. from a React unmount effect).
 */
export const debounce = <TArgs extends unknown[], TResult>(
	fn: (...args: TArgs) => TResult | Promise<TResult>,
	delay: number
): Debounced<TArgs, TResult> => {
	let timer: ReturnType<typeof setTimeout> | null = null;

	const debounced = ((...args: TArgs): Promise<TResult> => {
		if (timer) clearTimeout(timer);
		return new Promise<TResult>((resolve, reject) => {
			timer = setTimeout(async () => {
				try {
					resolve(await fn(...args));
				} catch (err) {
					reject(err);
				}
			}, delay);
		});
	}) as Debounced<TArgs, TResult>;

	debounced.cancel = () => {
		if (timer) {
			clearTimeout(timer);
			timer = null;
		}
	};

	return debounced;
};
